import { useState, useEffect } from 'react';
import { RefreshCw, X, Loader2, Download, CheckCircle2, Menu } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL;

export const SqueegeeSync = ({ onClose, onImported, onMenuClick }) => {
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [result, setResult] = useState(null);

  const load = () => {
    setLoading(true);
    fetch(`${API}/api/squeegee/missing`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.json())
      .then(d => {
        if (d.detail) throw new Error(d.detail);
        setData(d);
        setRows((d.missing || []).map(m => ({
          ...m,
          selected: false,
          round: m.suggested_round || '',
          frequency_weeks: 4,
          first_date: '',
          price: m.price ?? '',
        })));
      })
      .catch(e => toast.error(`Squeegee check failed: ${e.message}`))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const update = (i, patch) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const selected = rows.filter(r => r.selected);
  const missingDates = selected.filter(r => !r.first_date);

  const doImport = async () => {
    if (!selected.length || missingDates.length) return;
    setImporting(true);
    try {
      const res = await fetch(`${API}/api/squeegee/import-selected`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          records: selected.map(r => ({
            squeegee_id: r.squeegee_id,
            name: r.name,
            address: r.address,
            email: r.email,
            phone: r.phone,
            price: parseFloat(r.price) || 0,
            round: r.round,
            frequency_weeks: parseInt(r.frequency_weeks) || null,
            first_date: r.first_date,
          })),
        }),
      });
      const d = await res.json();
      if (d.detail) throw new Error(d.detail);
      setResult(d);
      toast.success(`Imported ${d.created_customers} customer${d.created_customers !== 1 ? 's' : ''}`);
      onImported && onImported();
      load();
    } catch (e) {
      toast.error(`Import failed: ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-50 flex flex-col" data-testid="squeegee-sync-modal">
      <div
        className="bg-slate-800 text-white px-4 py-3 flex items-center justify-between shrink-0"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          {onMenuClick && (
            <button onClick={onMenuClick} className="p-2 -ml-1 rounded-lg hover:bg-slate-700" data-testid="squeegee-menu-btn">
              <Menu size={22} />
            </button>
          )}
          <div className="min-w-0">
            <h2 className="font-bold text-lg leading-tight">Squeegee Sync</h2>
            <p className="text-slate-300 text-xs truncate">
              {data ? `${data.total_active} active · ${data.matched} matched · ${rows.length} new to review` : 'Checking Squeegee…'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={load} disabled={loading} className="p-2 rounded-lg hover:bg-slate-700" data-testid="squeegee-refresh-btn">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-700" data-testid="squeegee-close-btn">
            <X size={20} />
          </button>
        </div>
      </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500 gap-2">
              <Loader2 className="animate-spin" size={20} /> Reading your Squeegee customers…
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center py-16 text-slate-500 gap-2">
              <CheckCircle2 size={32} className="text-emerald-500" />
              <p className="font-medium text-slate-700">No new Squeegee customers to review</p>
              <p className="text-xs text-center max-w-sm">
                Anyone added to Squeegee from today onwards will show here. Checked automatically
                every night at 11pm — you'll get a notification when someone new appears.
                {data?.baselined ? ` (${data.baselined} earlier customers are set aside for your CSV import.)` : ''}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selected.length === rows.length && rows.length > 0}
                    onChange={e => setRows(rs => rs.map(r => ({ ...r, selected: e.target.checked })))}
                    className="w-4 h-4 accent-emerald-600"
                    data-testid="squeegee-select-all"
                  />
                  Select all
                </label>
                <span className="text-slate-500">{selected.length} of {rows.length} selected</span>
              </div>
              {rows.map((r, i) => (
                <div key={r.squeegee_id || i} className={`border rounded-xl p-3 ${r.selected ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200'}`} data-testid={`squeegee-row-${i}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={r.selected}
                      onChange={e => update(i, { selected: e.target.checked })}
                      className="w-4 h-4 mt-1 accent-emerald-600"
                      data-testid={`squeegee-row-check-${i}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800">{r.name}</p>
                      <p className="text-sm text-slate-500">{r.address}</p>
                      {(r.email || r.phone) && (
                        <p className="text-xs text-slate-400">{[r.phone, r.email].filter(Boolean).join(' · ')}</p>
                      )}
                      {r.squeegee_group && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          Squeegee group: {r.squeegee_group}
                          {r.first_seen && <> · appeared {new Date(r.first_seen).toLocaleDateString('en-GB')}</>}
                        </p>
                      )}
                    </div>
                  </div>
                  {r.selected && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                      <div>
                        <label className="text-[11px] text-slate-500">Price £</label>
                        <input type="number" step="0.5" value={r.price} onChange={e => update(i, { price: e.target.value })}
                          className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm" data-testid={`squeegee-price-${i}`} />
                      </div>
                      <div>
                        <label className="text-[11px] text-slate-500">Round</label>
                        <select value={r.round} onChange={e => update(i, { round: e.target.value })}
                          className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white" data-testid={`squeegee-round-${i}`}>
                          <option value="">No round</option>
                          {(data?.rounds || []).map(n => <option key={n} value={n}>{n}</option>)}
                          {r.round && !(data?.rounds || []).includes(r.round) && <option value={r.round}>{r.round}</option>}
                        </select>
                      </div>
                      <div>
                        <label className="text-[11px] text-slate-500">Every (weeks)</label>
                        <input type="number" min="0" value={r.frequency_weeks} onChange={e => update(i, { frequency_weeks: e.target.value })}
                          className="w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm" data-testid={`squeegee-freq-${i}`} />
                      </div>
                      <div>
                        <label className="text-[11px] text-slate-500">First due *</label>
                        <input type="date" value={r.first_date} onChange={e => update(i, { first_date: e.target.value })}
                          className={`w-full border rounded-lg px-2 py-1.5 text-sm ${r.first_date ? 'border-slate-300' : 'border-red-300 bg-red-50'}`}
                          data-testid={`squeegee-date-${i}`} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {result && (
            <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-800" data-testid="squeegee-import-result">
              Imported {result.created_customers} customers, {result.created_jobs} jobs
              {result.rounds_created ? `, ${result.rounds_created} new rounds` : ''}.
              {result.skipped?.length > 0 && <span className="block text-amber-700 mt-1">Skipped: {result.skipped.join('; ')}</span>}
            </div>
          )}
        </div>

        {rows.length > 0 && (
          <div
            className="border-t border-slate-200 bg-white p-3 flex items-center justify-between gap-3 shrink-0"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
          >
            <p className="text-xs text-slate-500">
              {missingDates.length > 0 && selected.length > 0
                ? `${missingDates.length} selected row${missingDates.length !== 1 ? 's' : ''} need a first-due date`
                : 'Nothing is created on Squeegee — this only adds to StreakFree'}
            </p>
            <button
              onClick={doImport}
              disabled={!selected.length || missingDates.length > 0 || importing}
              className="px-4 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold text-sm disabled:opacity-40 flex items-center gap-2"
              data-testid="squeegee-import-btn"
            >
              {importing ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
              Import {selected.length || ''} selected
            </button>
          </div>
        )}
    </div>
  );
};

export default SqueegeeSync;
